import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import {
  CheckCircle,
  Info,
  SpinnerGap,
  WarningCircle,
  X,
  XCircle,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import {
  dismissToast,
  getToastSnapshot,
  subscribeToToasts,
  type ToastRecord,
  type ToastVariant,
} from "@/lib/toast";
import { cn } from "@/utils/cn";

function ToastStatusIcon({ variant }: { variant: ToastVariant }) {
  const iconClassName = "size-[18px] shrink-0";

  if (variant === "loading") {
    return (
      <SpinnerGap
        aria-hidden="true"
        className={cn(iconClassName, "ours-toast__spinner")}
      />
    );
  }
  if (variant === "success") {
    return (
      <CheckCircle aria-hidden="true" className={iconClassName} weight="bold" />
    );
  }
  if (variant === "warning") {
    return (
      <WarningCircle
        aria-hidden="true"
        className={iconClassName}
        weight="bold"
      />
    );
  }
  if (variant === "error") {
    return (
      <XCircle aria-hidden="true" className={iconClassName} weight="bold" />
    );
  }
  return <Info aria-hidden="true" className={iconClassName} weight="bold" />;
}

function ToastItem({ item }: { item: ToastRecord }) {
  const { t } = useTranslation();
  const timerRef = useRef<number | undefined>(undefined);

  const pauseDismissal = useCallback(() => {
    if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
  }, []);

  const scheduleDismissal = useCallback(() => {
    pauseDismissal();
    if (item.duration === false) return;
    timerRef.current = window.setTimeout(
      () => dismissToast(item.id),
      item.duration,
    );
  }, [item.duration, item.id, pauseDismissal]);

  useEffect(() => {
    scheduleDismissal();
    return pauseDismissal;
  }, [pauseDismissal, scheduleDismissal]);

  const label = {
    error: t("toast.error"),
    info: t("toast.info"),
    loading: t("toast.loading"),
    success: t("toast.success"),
    warning: t("toast.warning"),
  }[item.variant];

  return (
    <div
      className={cn("ours-toast", `ours-toast--${item.variant}`)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          scheduleDismissal();
      }}
      onFocusCapture={pauseDismissal}
      onMouseEnter={pauseDismissal}
      onMouseLeave={scheduleDismissal}
      role={item.variant === "error" ? "alert" : "status"}
    >
      <div aria-hidden="true" className="ours-toast__icon">
        <ToastStatusIcon variant={item.variant} />
      </div>
      <div className="ours-toast__content">
        <div className="ours-toast__label">{label}</div>
        <div className="ours-toast__title">{item.title}</div>
        {item.description ? (
          <div className="ours-toast__description">{item.description}</div>
        ) : null}
        {item.action ? (
          <button
            className="ours-toast__action"
            onClick={() => {
              item.action?.onClick();
              dismissToast(item.id);
            }}
            type="button"
          >
            {item.action.label}
          </button>
        ) : null}
      </div>
      <button
        aria-label={t("toast.dismiss")}
        className="ours-toast__close"
        onClick={() => dismissToast(item.id)}
        type="button"
      >
        <X aria-hidden="true" size={16} weight="bold" />
      </button>
    </div>
  );
}

export function ToastViewport() {
  const { t } = useTranslation();
  const toasts = useSyncExternalStore(
    subscribeToToasts,
    getToastSnapshot,
    getToastSnapshot,
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  return createPortal(
    <section aria-label={t("toast.region")} className="ours-toast-viewport">
      {toasts.map((item) => (
        <ToastItem item={item} key={item.id} />
      ))}
    </section>,
    document.body,
  );
}
